
import { useEffect, useRef } from 'react';
import { useAnalytics } from './useAnalytics';

export const usePageTracking = (pageName: string) => {
  const { trackPageView, trackScrollDepth, trackUserEngagement } = useAnalytics();
  const scrollDepthTracked = useRef<Set<number>>(new Set());
  const engagementTracked = useRef<Set<number>>(new Set());
  const startTime = useRef<number>(Date.now());

  useEffect(() => {
    // Track page view
    trackPageView(pageName);
    startTime.current = Date.now();

    // Scroll depth tracking
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      // Track scroll milestones
      [25, 50, 75, 100].forEach(milestone => {
        if (scrollPercent >= milestone && !scrollDepthTracked.current.has(milestone)) {
          scrollDepthTracked.current.add(milestone);
          trackScrollDepth(milestone);
        }
      });
    };

    // Time engagement tracking
    const trackEngagement = () => {
      const timeOnPage = Math.round((Date.now() - startTime.current) / 1000);
      
      [30, 60, 120].forEach(milestone => {
        if (timeOnPage >= milestone && !engagementTracked.current.has(milestone)) {
          engagementTracked.current.add(milestone);
          trackUserEngagement(milestone);
        }
      });
    };

    // Event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    const engagementInterval = setInterval(trackEngagement, 5000);

    // Error tracking
    const handleError = (event: ErrorEvent) => {
      console.error('JavaScript Error:', event.error);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled Promise Rejection:', event.reason);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      clearInterval(engagementInterval);
    };
  }, [pageName, trackPageView, trackScrollDepth, trackUserEngagement]);

  return {
    getTimeOnPage: () => Math.round((Date.now() - startTime.current) / 1000)
  };
};
